import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search, Edit2, Trash2 } from "lucide-react";
import Wrapper from "../../../components/Wrapper";
import { hardwareApi } from "../../../services/labApi";

export default function HardwareOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [hardwareList, setHardwareList] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showFullSetModal, setShowFullSetModal] = useState(false);
  
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({ customer_name: "", due_date: "", notes: "", number_of_sets: "" });

  const [customForm, setCustomForm] = useState({ order_number: "", customer_name: "", due_date: "", notes: "" });
  const [fullSetForm, setFullSetForm] = useState({ order_number: "", customer_name: "", number_of_sets: "", due_date: "", notes: "" });
  const [orderItems, setOrderItems] = useState([{ hardware_id: "", quantity_required: "" }]);
  
  const [fulfillDrafts, setFulfillDrafts] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);
  const [actionError, setActionError] = useState("");

  const deviceOptions = hardwareList.filter(h => h.name.toLowerCase().includes('device'));
  const shakerOptions = hardwareList.filter(h => h.name.toLowerCase().includes('shaker'));
  const pumpOptions = hardwareList.filter(h => h.name.toLowerCase().includes('pump'));

  const itemRowId = (item) => item?.item_id;

  const load = useCallback(async () => {
    const [orderRows, hwRows] = await Promise.all([
      hardwareApi.getOrders({
        status: statusFilter === "all" ? undefined : statusFilter,
        search,
      }),
      hardwareApi.getHardware(),
    ]);

    setOrders(orderRows);
    setHardwareList(hwRows);

    const drafts = {};
    orderRows.forEach((o) => {
      (o.items || []).forEach((it) => {
        const rid = itemRowId(it);
        if (rid != null) drafts[rid] = String(it.quantity_fulfilled ?? 0);
      });
    });
    setFulfillDrafts(drafts);
  }, [statusFilter, search]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const generateOrderNumber = () => {
    const date = new Date().toISOString().slice(2, 10).replace(/-/g, "");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `HW${date}${random}`;
  };

  const openCustom = () => {
    setCustomForm({ order_number: generateOrderNumber(), customer_name: "", due_date: "", notes: "" });
    setOrderItems([{ hardware_id: "", quantity_required: "" }]);
    setShowCustomModal(true);
  };

  const openFullSet = () => {
    setFullSetForm({ 
        order_number: generateOrderNumber(), 
        customer_name: "", 
        number_of_sets: "", 
        due_date: "", 
        notes: "",
        device_id: deviceOptions.length === 1 ? deviceOptions[0].id : "",
        shaker_id: shakerOptions.length === 1 ? shakerOptions[0].id : "",
        pump_id: pumpOptions.length === 1 ? pumpOptions[0].id : ""
    });
    setShowFullSetModal(true);
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setEditForm({
      customer_name: order.customer_name || "",
      due_date: order.due_date ? String(order.due_date).split('T')[0] : "",
      notes: order.notes || "",
    });
  };

  const createCustom = async (e) => {
    e.preventDefault();
    try {
      await hardwareApi.createCustomOrder({
        ...customForm,
        items: orderItems
          .filter((i) => i.hardware_id && Number(i.quantity_required) > 0)
          .map((i) => ({ hardware_id: i.hardware_id, quantity_required: Number(i.quantity_required) })),
      });
      setShowCustomModal(false);
      setActionError("");
      await load();
    } catch (error) {
      setActionError(error?.response?.data?.message || "Unable to create custom order.");
    }
  };

  const createFullSet = async (e) => {
    e.preventDefault();
    if (!fullSetForm.due_date) {
      setActionError("Due date is required for full set orders.");
      return;
    }

    if (!fullSetForm.device_id || !fullSetForm.shaker_id || !fullSetForm.pump_id) {
        setActionError("Please select a version for the Device, Shaker, and Pump.");
        return;
    }
    
    try {
      await hardwareApi.createFullSetOrder({
        ...fullSetForm,
        number_of_sets: Number(fullSetForm.number_of_sets || 0),
        selected_ids: [fullSetForm.device_id, fullSetForm.shaker_id, fullSetForm.pump_id]
      });
      setActionError("");
      setShowFullSetModal(false);
      await load();
    } catch (error) {
      setActionError(error?.response?.data?.message || "Unable to create full set order.");
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      setActionError("");
      await hardwareApi.updateOrderDetails(editingOrder.id, {
        customer_name: editForm.customer_name,
        due_date: editForm.due_date || null,
        notes: editForm.notes || null,
      });
      setEditingOrder(null);
      await load();
    } catch (error) {
      setActionError(error?.response?.data?.message || "Failed to update order details.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      setActionError("");
      await hardwareApi.updateOrderStatus(id, status);
      await load();
    } catch (error) {
      setActionError(error?.response?.data?.message || "Failed to update order status.");
    }
  };

  const handleDeleteOrder = async (id, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete hardware order ${orderNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setActionError("");
      await hardwareApi.deleteOrder(id);
      await load();
    } catch (error) {
      setActionError(error?.response?.data?.message || "Failed to delete hardware order.");
    }
  };

  const saveFulfilled = async (item) => {
    const rowId = itemRowId(item);
    if (rowId == null) return;
    const raw = fulfillDrafts[rowId];
    const qty = Number(raw);
    
    if (Number.isNaN(qty) || qty < 0) {
      setActionError("Enter a valid fulfilled quantity (0 or more).");
      return;
    }
    
    setActionError("");
    try {
      setSavingItemId(rowId);
      await hardwareApi.updateOrderItemFulfillment(rowId, qty);
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || "Could not update fulfilled quantity.");
    } finally {
      setSavingItemId(null);
    }
  };

  const displayedOrders = orders.filter(o => {
    const matchesSearch = search === "" || 
      o.order_number.toLowerCase().includes(search.toLowerCase()) || 
      (o.customer_name && o.customer_name.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = statusFilter === "all" || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="w-full text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Hardware Orders</h2>
            <p className="mt-1 text-slate-500 text-left">Live hardware order tracking</p>
          </div>
          <div className="flex gap-2 self-start">
            <button onClick={openFullSet} className="bg-teal-600 text-white px-4 py-2 rounded-lg">Full Set Order</button>
            <button onClick={openCustom} className="bg-emerald-600 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2">
              <Plus size={16} /> Custom Order
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." className="w-full pl-10 pr-4 py-2 border rounded-lg" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="shipped">Shipped</option>
            </select>
          </div>
          {actionError ? <p className="text-sm text-red-600 font-medium">{actionError}</p> : null}
        </div>

        <div className="space-y-3">
          {displayedOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    {order.order_number}
                    <button onClick={() => openEditModal(order)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-emerald-600 transition-colors">
                      <Edit2 size={14} />
                    </button>
                    <button 
                      onClick={() => handleDeleteOrder(order.id, order.order_number)} 
                      className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-red-600 transition-colors"
                      title="Delete Order"
                    >
                      <Trash2 size={14} />
                    </button>
                  </h3>
                  <p className="text-sm text-slate-500">
                    {order.customer_name || 'No Customer Specified'}
                    {order.due_date ? ` • Due: ${String(order.due_date).split('T')[0]}` : ""}
                  </p>
                  {order.notes && <p className="text-xs text-slate-400 italic mt-0.5">Notes: {order.notes}</p>}
                </div>
                
                <select 
                  value={order.status || 'pending'} 
                  onChange={(e) => setStatus(order.id, e.target.value)} 
                  disabled={String(order.status || "").toLowerCase() === "completed"}
                  className="px-2 py-1.5 border rounded text-sm bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="shipped">Shipped</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              {Number(order.total_required) > 0 ? (
                <p className="mt-2 text-xs text-slate-500 bg-slate-100 inline-block px-2 py-1 rounded">
                  Progress: {Number(order.total_fulfilled || 0)} / {Number(order.total_required)} units fulfilled
                </p>
              ) : null}
              
              <div className="mt-3 space-y-2">
                {(order.items || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No line items</p>
                ) : null}
                
                {(order.items || []).map((item) => {
                  const rowId = itemRowId(item);
                  const draft = rowId != null ? fulfillDrafts[rowId] : undefined;
                  const displayDraft = draft !== undefined ? draft : String(item.quantity_fulfilled ?? 0);
                  
                  const hasEnoughStock = Number(item.current_stock || 0) >= Number(item.quantity_required || 0);
                  const stockColorClasses = hasEnoughStock ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30";

                  return (
                    <div
                      key={rowId ?? `${item.hardware_id}-${item.hardware_name}`}
                      className={`flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between transition-colors ${stockColorClasses}`}
                    >
                      <div className="text-sm text-slate-700">
                        <span className="font-medium">
                          {item.hardware_name || item.hardware_id} 
                          {item.version_tag && <span className="text-emerald-600 ml-1">({item.version_tag})</span>}
                        </span>
                        <span className="text-slate-500"> — {item.quantity_required} required</span>
                        <span className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded ${hasEnoughStock ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          Stock: {item.current_stock || 0}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                          <span>Fulfilled</span>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            disabled={rowId == null || savingItemId === rowId || String(order.status || "").toLowerCase() === "completed"}
                            value={displayDraft}
                            onChange={(e) =>
                              rowId != null &&
                              setFulfillDrafts((prev) => ({ ...prev, [rowId]: e.target.value }))
                            }
                            className="w-24 rounded border border-slate-200 px-2 py-1 text-slate-800 bg-white disabled:opacity-50"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={rowId == null || savingItemId === rowId || String(order.status || "").toLowerCase() === "completed"}
                          onClick={() => saveFulfilled(item)}
                          className="rounded-lg bg-slate-800 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
                        >
                          {savingItemId === rowId ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {displayedOrders.length === 0 && (
            <div className="text-center py-10 text-slate-500 border rounded-xl bg-slate-50">
              No orders found.
            </div>
          )}
        </div>

        {editingOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Edit Hardware Order: {editingOrder.order_number}</h3>
              <form onSubmit={submitEdit} className="space-y-3">
                <input value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} placeholder="Customer Name" className="w-full px-3 py-2 border rounded-lg" required />
                <input value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} type="date" className="w-full px-3 py-2 border rounded-lg text-slate-700" />
                
                <textarea 
                  value={editForm.notes} 
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} 
                  placeholder="Order Notes" 
                  className="w-full px-3 py-2 border rounded-lg h-24 resize-none" 
                />
                
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setEditingOrder(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white rounded-lg">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

                {/* --- CUSTOM ORDER MODAL --- */}
        {showCustomModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-3">Create Custom Order</h3>
              <form onSubmit={createCustom} className="space-y-3">
                <input value={customForm.order_number} onChange={(e) => setCustomForm({ ...customForm, order_number: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-slate-50" required readOnly />
                <input value={customForm.customer_name} onChange={(e) => setCustomForm({ ...customForm, customer_name: e.target.value })} placeholder="Customer Name" className="w-full px-3 py-2 border rounded-lg" required />
                <input value={customForm.due_date} onChange={(e) => setCustomForm({ ...customForm, due_date: e.target.value })} type="date" className="w-full px-3 py-2 border rounded-lg text-slate-700" />
                
                <div className="pt-2 border-t space-y-2">
                  <p className="text-sm font-medium text-slate-700">Order Items</p>
                  {orderItems.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <select 
                        value={item.hardware_id} 
                        onChange={(e) => setOrderItems((prev) => prev.map((row, i) => i === idx ? { ...row, hardware_id: e.target.value } : row))} 
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="">Select Hardware</option>
                        {hardwareList.map((h) => <option key={h.id} value={h.id}>{h.name} ({h.version_tag})</option>)}
                      </select>
                      <input 
                        value={item.quantity_required} 
                        onChange={(e) => setOrderItems((prev) => prev.map((row, i) => i === idx ? { ...row, quantity_required: e.target.value } : row))} 
                        type="number" 
                        min="1"
                        placeholder="Qty" 
                        className="w-24 px-3 py-2 border rounded-lg text-sm" 
                      />
                    </div>
                  ))}
                  <button type="button" onClick={() => setOrderItems((prev) => [...prev, { hardware_id: "", quantity_required: "" }])} className="text-sm text-emerald-600 font-medium hover:text-emerald-700 p-1">+ Add Item</button>
                </div>

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowCustomModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">Create Order</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- FULL SET ORDER MODAL --- */}
        {showFullSetModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Create Full Set Order</h3>
              <form onSubmit={createFullSet} className="space-y-3">
                <input value={fullSetForm.order_number} onChange={(e) => setFullSetForm({ ...fullSetForm, order_number: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-slate-50" required readOnly />
                <input value={fullSetForm.customer_name} onChange={(e) => setFullSetForm({ ...fullSetForm, customer_name: e.target.value })} placeholder="Customer Name" className="w-full px-3 py-2 border rounded-lg" required />
                <input value={fullSetForm.number_of_sets} onChange={(e) => setFullSetForm({ ...fullSetForm, number_of_sets: e.target.value })} type="number" min="1" placeholder="Number of Sets" className="w-full px-3 py-2 border rounded-lg" required />
                <div className="p-3 bg-slate-50 border rounded-lg space-y-3 my-2">
                  <p className="text-sm font-semibold text-slate-700">Select Hardware Versions</p>
                  
                  {/* Device Dropdown */}
                  <select 
                      value={fullSetForm.device_id} 
                      onChange={e => setFullSetForm({...fullSetForm, device_id: e.target.value})} 
                      className="w-full px-3 py-2 border rounded-lg text-sm" required
                  >
                      <option value="">Select Device Version...</option>
                      {deviceOptions.map(h => <option key={h.id} value={h.id}>{h.name} ({h.version_tag})</option>)}
                  </select>

                  {/* Shaker Dropdown */}
                  <select 
                      value={fullSetForm.shaker_id} 
                      onChange={e => setFullSetForm({...fullSetForm, shaker_id: e.target.value})} 
                      className="w-full px-3 py-2 border rounded-lg text-sm" required
                  >
                      <option value="">Select Shaker Version...</option>
                      {shakerOptions.map(h => <option key={h.id} value={h.id}>{h.name} ({h.version_tag})</option>)}
                  </select>

                  {/* Pump Dropdown */}
                  <select 
                      value={fullSetForm.pump_id} 
                      onChange={e => setFullSetForm({...fullSetForm, pump_id: e.target.value})} 
                      className="w-full px-3 py-2 border rounded-lg text-sm" required
                  >
                      <option value="">Select Pump Version...</option>
                      {pumpOptions.map(h => <option key={h.id} value={h.id}>{h.name} ({h.version_tag})</option>)}
                  </select>
                </div>
                <input value={fullSetForm.due_date} onChange={(e) => setFullSetForm({ ...fullSetForm, due_date: e.target.value })} type="date" className="w-full px-3 py-2 border rounded-lg text-slate-700" required />
                
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowFullSetModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg">Create Order</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}